import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';
import { Button } from '../components/ui/button';
import { MessageSquare, X } from 'lucide-react';
import ReservationChecklist from '../components/ReservationChecklist';
import ViewResourceProfileButton from '../components/ViewResourceProfileButton';
import { navigateWithState, clearNavigationState } from '../utils/navigationState';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReservationsPage = () => {
  const { user, company } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  
  const [incomingReservations, setIncomingReservations] = useState([]);
  const [outgoingReservations, setOutgoingReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('incoming');
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingId, setCancellingId] = useState(null);
  const [highlightedReservation, setHighlightedReservation] = useState(null);
  const reservationRefs = useRef({});

  // Restore state when returning from navigation
  useEffect(() => {
    if (location.state?.restoreState) {
      if (location.state.activeTab) {
        setActiveTab(location.state.activeTab);
      }
      if (location.state.scrollPosition) {
        setTimeout(() => {
          window.scrollTo(0, location.state.scrollPosition);
        }, 100);
      }
      // Clear the location state to prevent re-restoration
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (user) {
      fetchReservations();
    }
  }, [user]);

  // Handle reservation highlighting from query parameter
  useEffect(() => {
    const reservationId = searchParams.get('reservation');
    if (reservationId && (incomingReservations.length > 0 || outgoingReservations.length > 0)) {
      // Check if reservation is in incoming or outgoing
      const isIncoming = incomingReservations.some(r => r.reservation.id === reservationId);
      const isOutgoing = outgoingReservations.some(r => r.reservation.id === reservationId);
      
      if (isIncoming) {
        setActiveTab('incoming');
      } else if (isOutgoing) {
        setActiveTab('outgoing');
      }
      
      // Highlight the reservation
      setHighlightedReservation(reservationId);
      
      // Scroll to the reservation after a short delay to ensure rendering
      setTimeout(() => {
        const element = reservationRefs.current[reservationId];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
      
      // Remove highlight after 3 seconds
      setTimeout(() => {
        setHighlightedReservation(null);
      }, 3000);
    }
  }, [searchParams, incomingReservations, outgoingReservations]);

  const fetchReservations = async () => {
    const token = localStorage.getItem('token');
    try {
      const [incomingRes, outgoingRes] = await Promise.all([
        axios.get(`${API}/pre-reservations/incoming`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/pre-reservations/outgoing`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setIncomingReservations(incomingRes.data);
      setOutgoingReservations(outgoingRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching reservations:', error);
      toast({
        title: "Error",
        description: "Failed to load reservations",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const handleApprove = async (reservationId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/pre-reservations/${reservationId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "✅ Reservation Approved",
        description: "The reservation has been approved successfully."
      });
      
      fetchReservations();
    } catch (error) {
      console.error('Error approving reservation:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to approve reservation",
        variant: "destructive"
      });
    }
  };

  const handleReject = async (reservationId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/pre-reservations/${reservationId}/reject?reason=${encodeURIComponent(rejectReason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: "Reservation Rejected",
        description: "The reservation has been declined."
      });
      
      setRejectingId(null);
      setRejectReason('');
      fetchReservations();
    } catch (error) {
      console.error('Error rejecting reservation:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to reject reservation",
        variant: "destructive"
      });
    }
  };

  const handleCancel = async (reservationId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/pre-reservations/${reservationId}/cancel?reason=${encodeURIComponent(cancelReason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: "Reservation Cancelled",
        description: "The reservation has been cancelled."
      });
      
      setCancellingId(null);
      setCancelReason('');
      fetchReservations();
    } catch (error) {
      console.error('Error cancelling reservation:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to cancel reservation",
        variant: "destructive"
      });
    }
  };

  const viewConversation = (threadId) => {
    navigateWithState(navigate, `/messages?thread=${threadId}`, {
      path: '/reservations',
      activeTab,
      pageName: 'Réservations'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (!user) {
    return <Navigate to="/auth" />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-sn-navy">Reservations</h1>
          <p className="mt-2 text-gray-600">
            Manage your reservation requests and responses
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('incoming')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'incoming'
                  ? 'border-sn-green text-sn-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Incoming Requests
              {incomingReservations.length > 0 && (
                <span className="ml-2 bg-sn-green text-white rounded-full px-2 py-0.5 text-xs">
                  {incomingReservations.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('outgoing')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'outgoing'
                  ? 'border-sn-green text-sn-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Sent Requests
              {outgoingReservations.length > 0 && (
                <span className="ml-2 bg-gray-500 text-white rounded-full px-2 py-0.5 text-xs">
                  {outgoingReservations.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-sn-green"></div>
            <p className="mt-2 text-gray-600">Loading reservations...</p>
          </div>
        ) : (
          <>
            {/* Incoming Requests */}
            {activeTab === 'incoming' && (
              <div className="space-y-4">
                {incomingReservations.length === 0 ? (
                  <div className="bg-white rounded-lg shadow p-8 text-center">
                    <p className="text-gray-500">No incoming reservation requests</p>
                  </div>
                ) : (
                  incomingReservations.map(({ reservation, resource, project, from_company }) => {
                    console.log('🔍 Incoming Reservation Resource:', { 
                      resourceId: resource?.id, 
                      resourceName: resource?.name 
                    });
                    const isHighlighted = highlightedReservation === reservation.id;
                    return (
                    <div 
                      key={reservation.id} 
                      ref={el => reservationRefs.current[reservation.id] = el}
                      className={`bg-white rounded-lg shadow-md p-6 transition-all duration-500 ${
                        isHighlighted ? 'ring-4 ring-green-400 shadow-2xl' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-sn-navy">
                              {resource?.name || 'Resource'}
                            </h3>
                            {getStatusBadge(reservation.status)}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                            <div>
                              <p className="text-gray-600">Resource Details:</p>
                              <p className="font-medium">{resource?.role} - {resource?.seniority}</p>
                              <p className="text-gray-500">{resource?.city}, {resource?.country}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Project:</p>
                              <p className="font-medium">{project?.title}</p>
                              <p className="text-gray-500">
                                By: <button
                                  onClick={() => navigateWithState(navigate, `/company/${from_company?.id}`, {
                                    path: '/reservations',
                                    activeTab,
                                    pageName: 'Réservations'
                                  })}
                                  className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                >
                                  {from_company?.name}
                                </button>
                              </p>
                            </div>
                          </div>
                          
                          <p className="text-xs text-gray-500 mt-4">
                            Requested: {new Date(reservation.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="mt-6 flex items-center space-x-3">
                        <ViewResourceProfileButton 
                          resourceId={resource?.id}
                          variant="outline"
                          size="sm"
                          navigationState={{
                            path: '/reservations',
                            activeTab,
                            pageName: 'Réservations'
                          }}
                        />
                        
                        {reservation.status === 'pending' ? (
                          <>
                            <button
                              onClick={() => handleApprove(reservation.id)}
                              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                            >
                              ✓ Approve
                            </button>
                            
                            {rejectingId === reservation.id ? (
                              <div className="flex items-center space-x-2 flex-1">
                                <input
                                  type="text"
                                  placeholder="Reason for rejection (optional)"
                                  value={rejectReason}
                                  onChange={(e) => setRejectReason(e.target.value)}
                                  className="flex-1 border rounded-md px-3 py-2 text-sm"
                                />
                                <button
                                  onClick={() => handleReject(reservation.id)}
                                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                                >
                                  Confirm Reject
                                </button>
                                <button
                                  onClick={() => {
                                    setRejectingId(null);
                                    setRejectReason('');
                                  }}
                                  className="text-gray-600 px-3 py-2 hover:text-gray-800"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setRejectingId(reservation.id)}
                                className="border border-red-600 text-red-600 px-4 py-2 rounded-md hover:bg-red-50 transition-colors"
                              >
                                ✗ Reject
                              </button>
                            )}
                          </>
                        ) : null}
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewConversation(reservation.conversation_thread_id)}
                          className="ml-auto"
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          View Conversation
                        </Button>
                      </div>

                      {/* Show checklist if accepted */}
                      {reservation.status === 'accepted' && (
                        <ReservationChecklist reservationId={reservation.id} />
                      )}
                    </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Outgoing Requests */}
            {activeTab === 'outgoing' && (
              <div className="space-y-4">
                {outgoingReservations.length === 0 ? (
                  <div className="bg-white rounded-lg shadow p-8 text-center">
                    <p className="text-gray-500">No sent reservation requests</p>
                  </div>
                ) : (
                  outgoingReservations.map(({ reservation, resource, project, to_company }) => {
                    console.log('🔍 Outgoing Reservation Resource:', { 
                      resourceId: resource?.id, 
                      resourceName: resource?.name 
                    });
                    const isHighlighted = highlightedReservation === reservation.id;
                    return (
                    <div 
                      key={reservation.id}
                      ref={el => reservationRefs.current[reservation.id] = el}
                      className={`bg-white rounded-lg shadow-md p-6 transition-all duration-500 ${
                        isHighlighted ? 'ring-4 ring-green-400 shadow-2xl' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-sn-navy">
                              {resource?.name || 'Resource'}
                            </h3>
                            {getStatusBadge(reservation.status)}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                            <div>
                              <p className="text-gray-600">Resource Details:</p>
                              <p className="font-medium">{resource?.role} - {resource?.seniority}</p>
                              <p className="text-gray-500">
                                From: <button
                                  onClick={() => navigateWithState(navigate, `/company/${to_company?.id}`, {
                                    path: '/reservations',
                                    activeTab,
                                    pageName: 'Réservations'
                                  })}
                                  className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                >
                                  {to_company?.name}
                                </button>
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600">Project:</p>
                              <button
                                onClick={() => navigateWithState(navigate, `/projects/${project?.id}`, {
                                  path: '/reservations',
                                  activeTab,
                                  pageName: 'Réservations'
                                })}
                                className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                              >
                                {project?.title}
                              </button>
                              <p className="text-gray-500">{project?.city}, {project?.country}</p>
                            </div>
                          </div>
                          
                          <p className="text-xs text-gray-500 mt-4">
                            Requested: {new Date(reservation.created_at).toLocaleString()}
                            {reservation.updated_at && ` • Updated: ${new Date(reservation.updated_at).toLocaleString()}`}
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="mt-6 flex items-center space-x-3">
                        <ViewResourceProfileButton 
                          resourceId={resource?.id}
                          variant="outline"
                          size="sm"
                          navigationState={{
                            path: '/reservations',
                            activeTab,
                            pageName: 'Réservations'
                          }}
                        />
                        
                        {reservation.status === 'pending' && (
                          <>
                            {cancellingId === reservation.id ? (
                              <div className="flex items-center space-x-2 flex-1">
                                <input
                                  type="text"
                                  placeholder="Reason for cancellation (optional)"
                                  value={cancelReason}
                                  onChange={(e) => setCancelReason(e.target.value)}
                                  className="flex-1 border rounded-md px-3 py-2 text-sm"
                                />
                                <button
                                  onClick={() => handleCancel(reservation.id)}
                                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
                                >
                                  Confirm Cancel
                                </button>
                                <button
                                  onClick={() => {
                                    setCancellingId(null);
                                    setCancelReason('');
                                  }}
                                  className="text-gray-600 px-3 py-2 hover:text-gray-800"
                                >
                                  Back
                                </button>
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCancellingId(reservation.id)}
                              >
                                <X className="w-4 h-4 mr-2" />
                                Cancel Request
                              </Button>
                            )}
                          </>
                        )}
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewConversation(reservation.conversation_thread_id)}
                          className="ml-auto"
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          View Conversation
                        </Button>
                      </div>

                      {/* Show checklist if accepted */}
                      {reservation.status === 'accepted' && (
                        <ReservationChecklist reservationId={reservation.id} />
                      )}
                    </div>
                    );
                  })
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReservationsPage;
